/**
 * PromptTemplateEditor - Edit prompts within a specific named ChartMind template.
 * 
 * Reuses the same card-based prompt editing pattern as PromptEditor.jsx
 * but reads/writes to the templates subcollection.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Slider,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { contentCardSx } from '../../../../../styles/standaloneLayoutTokens';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { INPUT_MODES } from '../../../../../services/llm/chainExecutionService';
import { CHARTMIND_CARDS, CHARTMIND_CARD_LIST, chartmindAdminService } from '../../../../../services/chartmind';

const INPUT_MODE_INFO = {
  [INPUT_MODES.PREVIOUS_ONLY]: {
    label: 'Previous Output Only (A)',
    description: 'Prompt receives only the output from the previous position.',
  },
  [INPUT_MODES.PREVIOUS_PLUS_ORIGINAL]: {
    label: 'Previous + Original Input (B)',
    description: 'Prompt receives both the previous output and the original user input.',
  },
  [INPUT_MODES.FULL_CHAIN_CONTEXT]: {
    label: 'Full Chain Context (C)',
    description: 'Prompt receives all previous outputs and the original input.',
  },
};

const CARD_LABELS = {
  [CHARTMIND_CARDS.DDX]: 'Differential Diagnosis',
  [CHARTMIND_CARDS.DDX_FINAL]: 'DDX Final',
  [CHARTMIND_CARDS.DIAGNOSTIC_TESTS]: 'Diagnostic Tests',
  [CHARTMIND_CARDS.TREATMENT_OPTIONS]: 'Treatment Options',
  [CHARTMIND_CARDS.DISCHARGE_PLAN]: 'Discharge Plan',
  [CHARTMIND_CARDS.CLARIFYING_QUESTIONS]: 'Clarifying Questions',
  [CHARTMIND_CARDS.CHART_GENERATION]: 'Chart Generation',
  [CHARTMIND_CARDS.REQUIRED_SECTIONS_TRACKER]: 'Sections Tracker',
  [CHARTMIND_CARDS.TEST_SMART_CHOICES]: 'Test Smart Choices',
  [CHARTMIND_CARDS.CLINICAL_GUIDANCE]: 'Clinical Guidance',
  [CHARTMIND_CARDS.TRANSLATE]: 'Translation',
  [CHARTMIND_CARDS.PEERVIEW_CONSULTATION]: 'PeerView Consult',
};

const PromptTemplateEditor = ({
  adminId,
  template,
  userEmail,
  onBack,
  onSaved,
  showBackButton = true,
}) => {
  const [activeCardTab, setActiveCardTab] = useState(0);
  const [localPrompts, setLocalPrompts] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [nameChanged, setNameChanged] = useState(false);

  // Initialize from template
  useEffect(() => {
    if (template) {
      setLocalPrompts(template.prompts || {});
      setTemplateName(template.name || '');
    }
  }, [template]);

  const currentCardId = CHARTMIND_CARD_LIST[activeCardTab] || CHARTMIND_CARD_LIST[0];
  const currentPrompt = localPrompts[currentCardId] || {};

  const updateCurrentPrompt = useCallback((field, value) => {
    setLocalPrompts(prev => ({
      ...prev,
      [currentCardId]: {
        ...prev[currentCardId],
        [field]: value,
      },
    }));
  }, [currentCardId]);

  const handleSavePrompt = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    try {
      const promptConfig = localPrompts[currentCardId] || {};
      await chartmindAdminService.updateTemplatePrompt(
        adminId,
        template.id,
        currentCardId,
        promptConfig,
        userEmail,
      );
      setSuccess(`${CARD_LABELS[currentCardId] || currentCardId} prompt saved!`);
      setTimeout(() => setSuccess(''), 3000);
      onSaved?.();
    } catch (err) {
      console.error('[PromptTemplateEditor] save error:', err);
      setError('Failed to save prompt.');
    } finally {
      setIsSaving(false);
    }
  }, [adminId, template?.id, currentCardId, localPrompts, userEmail, onSaved]);

  const handleToggle = useCallback(async (cardId, enabled) => {
    try {
      await chartmindAdminService.toggleTemplatePrompt(adminId, template.id, cardId, enabled);
      setLocalPrompts(prev => ({
        ...prev,
        [cardId]: { ...prev[cardId], enabled },
      }));
      onSaved?.();
    } catch (err) {
      console.error('[PromptTemplateEditor] toggle error:', err);
      setError('Failed to toggle prompt.');
    }
  }, [adminId, template?.id, onSaved]);

  const handleSaveName = useCallback(async () => {
    if (!templateName.trim()) return;
    try {
      await chartmindAdminService.updateTemplateName(adminId, template.id, templateName.trim());
      setNameChanged(false);
      setSuccess('Template name updated!');
      setTimeout(() => setSuccess(''), 3000);
      onSaved?.();
    } catch (err) {
      console.error('[PromptTemplateEditor] name update error:', err);
      setError('Failed to update template name.');
    }
  }, [adminId, template?.id, templateName, onSaved]);

  if (!template) return null;

  return (
    <Box>
      {/* Header with optional back button */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        {showBackButton && (
          <Tooltip title="Back to template list">
            <IconButton onClick={onBack}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        )}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              variant="standard"
              value={templateName}
              onChange={(e) => {
                setTemplateName(e.target.value);
                setNameChanged(true);
              }}
              sx={{
                '& .MuiInput-input': { fontSize: '1.25rem', fontWeight: 600 },
              }}
            />
            {nameChanged && (
              <Button size="small" onClick={handleSaveName}>
                Save Name
              </Button>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            Invite code: {template.inviteCode}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Card tabs */}
      <Paper elevation={0} sx={{ ...contentCardSx, mb: 2 }}>
        <Tabs
          value={activeCardTab}
          onChange={(_, v) => setActiveCardTab(v)}
          variant="scrollable"
          scrollButtons="auto"
        >
          {CHARTMIND_CARD_LIST.map((cardId) => (
            <Tab
              key={cardId}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {CARD_LABELS[cardId] || cardId}
                  {localPrompts[cardId]?.enabled && localPrompts[cardId]?.prompt && (
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Paper>

      {/* Card prompt editor */}
      <Paper elevation={0} sx={{ ...contentCardSx, p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {CARD_LABELS[currentCardId] || currentCardId}
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={!!currentPrompt.enabled}
                onChange={(e) => handleToggle(currentCardId, e.target.checked)}
              />
            }
            label={currentPrompt.enabled ? 'Enabled' : 'Disabled'}
          />
        </Box>

        {/* Prompt text */}
        <TextField
          fullWidth
          multiline
          rows={10}
          label="Position 3 Prompt"
          value={currentPrompt.prompt || ''}
          onChange={(e) => updateCurrentPrompt('prompt', e.target.value)}
          disabled={!currentPrompt.enabled}
          placeholder={
            currentPrompt.enabled
              ? 'Enter your custom prompt here. This will refine the output from previous positions.'
              : 'Enable this prompt to edit.'
          }
          sx={{
            mb: 2,
            '& .MuiInputBase-root': { fontFamily: 'monospace', fontSize: '0.875rem' },
          }}
        />

        {/* Model config */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Model</InputLabel>
            <Select
              value={currentPrompt.model || 'gpt-4-turbo'}
              label="Model"
              onChange={(e) => updateCurrentPrompt('model', e.target.value)}
              disabled={!currentPrompt.enabled}
            >
              <MenuItem value="gemini-2.0-flash">Gemini 2.0 Flash</MenuItem>
              <MenuItem value="gemini-2.0-flash-lite">Gemini 2.0 Flash Lite</MenuItem>
              <MenuItem value="gpt-4.1-nano">GPT-4.1 Nano</MenuItem>
              <MenuItem value="gpt-4o-mini">GPT-4o Mini</MenuItem>
              <MenuItem value="gpt-4-turbo">GPT-4 Turbo</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Input Mode</InputLabel>
            <Select
              value={currentPrompt.inputMode || INPUT_MODES.PREVIOUS_PLUS_ORIGINAL}
              label="Input Mode"
              onChange={(e) => updateCurrentPrompt('inputMode', e.target.value)}
              disabled={!currentPrompt.enabled}
            >
              {Object.entries(INPUT_MODE_INFO).map(([mode, info]) => (
                <MenuItem key={mode} value={mode}>
                  {info.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Temperature */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Temperature: {currentPrompt.temperature ?? 0.7}
          </Typography>
          <Slider
            value={currentPrompt.temperature ?? 0.7}
            onChange={(_, v) => updateCurrentPrompt('temperature', v)}
            min={0}
            max={2}
            step={0.1}
            disabled={!currentPrompt.enabled}
            sx={{ maxWidth: 300 }}
          />
        </Box>

        {/* Max tokens */}
        <TextField
          type="number"
          size="small"
          label="Max Tokens"
          value={currentPrompt.maxTokens ?? 2000}
          onChange={(e) => updateCurrentPrompt('maxTokens', parseInt(e.target.value) || 2000)}
          disabled={!currentPrompt.enabled}
          sx={{ mb: 3 }}
        />

        {/* Save button */}
        <Box>
          <Button
            variant="contained"
            startIcon={isSaving ? <CircularProgress size={18} /> : <SaveIcon />}
            onClick={handleSavePrompt}
            disabled={isSaving || !currentPrompt.enabled}
          >
            {isSaving ? 'Saving...' : 'Save Prompt'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default PromptTemplateEditor;
